import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  acceptable: [""],
  caseSensitive: false,
  maxLen: 200,
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const acceptable =
    Array.isArray(body.acceptable) && body.acceptable.length
      ? body.acceptable.map((s) => String(s))
      : [""];
  const caseSensitive = !!body.caseSensitive;
  const maxLen = body.maxLen ?? 200;
  const layout = body.layout || "top-bottom";

  const points = item.points ?? 1;
  const status = item.status || "published";
  const tags = Array.isArray(item.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    acceptable,
    caseSensitive,
    maxLen,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function ShortTextModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const addAcceptable = () =>
    setForm((f) => ({ ...f, acceptable: [...f.acceptable, ""] }));
  const removeAcceptable = (idx) =>
    setForm((f) => ({
      ...f,
      acceptable: f.acceptable.filter((_, i) => i !== idx),
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("short_text.toasts.stem_required"));
      return;
    }
    const answers = form.acceptable.map((s) => s.trim()).filter(Boolean);
    if (answers.length === 0) {
      toast.error(t("short_text.toasts.need_one_answer"));
      return;
    }

    const payload = {
      kind: "short_text",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        acceptable: answers,
        caseSensitive: !!form.caseSensitive,
        maxLen: Number(form.maxLen) || undefined,
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("short_text.toasts.updated", "سؤال کوتاه با موفقیت ویرایش شد")
          : t("short_text.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {" "}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {" "}
        <div dir={isRtl ? "rtl" : "ltr"}>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("short_text.stem_fa")}
          </label>{" "}
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("short_text.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />{" "}
        </div>{" "}
        <div dir="ltr">
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("short_text.stem_en")}
          </label>{" "}
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("short_text.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />{" "}
        </div>{" "}
      </div>
      {/* Layout Selector */}{" "}
      <div>
        {" "}
        <label className="block text-sm font-medium mb-1">
          {t("short_text.layout_label")}
        </label>{" "}
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {" "}
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                {" "}
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />{" "}
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {" "}
                  {t(`short_text.layout_question_answer_${layout}`)}{" "}
                </label>{" "}
              </div>
            )
          )}{" "}
        </div>{" "}
      </div>{" "}
      <div>
        {" "}
        <div className="flex items-center justify-between mb-1">
          {" "}
          <label className="block text-sm font-medium">
            {t("short_text.acceptable")}
          </label>{" "}
          <button
            type="button"
            onClick={addAcceptable}
            className="text-blue-600 text-sm"
          >
            <Plus size={16} /> {t("short_text.add_answer")}{" "}
          </button>{" "}
        </div>{" "}
        <div className="space-y-2">
          {" "}
          {form.acceptable.map((ans, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {" "}
              <input
                className="flex-1 border rounded-lg px-3 py-2"
                placeholder={t("short_text.answer_ph")}
                value={ans}
                onChange={(e) => {
                  const next = [...form.acceptable];
                  next[idx] = e.target.value;
                  setForm((f) => ({ ...f, acceptable: next }));
                }}
              />{" "}
              {form.acceptable.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeAcceptable(idx)}
                  className="p-1 rounded-full text-red-600 hover:bg-red-100"
                >
                  <Trash2 size={16} />{" "}
                </button>
              )}{" "}
            </div>
          ))}{" "}
        </div>{" "}
      </div>{" "}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {" "}
        <label className="inline-flex items-center gap-2">
          {" "}
          <input
            type="checkbox"
            checked={form.caseSensitive}
            onChange={(e) =>
              setForm((f) => ({ ...f, caseSensitive: e.target.checked }))
            }
          />{" "}
          <span className="text-sm">{t("short_text.case_sensitive")}</span>{" "}
        </label>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("short_text.max_len")}
          </label>{" "}
          <input
            type="number"
            className="w-full border rounded-lg px-3 py-2"
            value={form.maxLen}
            onChange={(e) => setForm((f) => ({ ...f, maxLen: e.target.value }))}
          />{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("short_text.points")}
          </label>{" "}
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />{" "}
        </div>{" "}
      </div>{" "}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("short_text.status")}
          </label>{" "}
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            {" "}
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>{" "}
          </select>{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("short_text.tags")}
          </label>{" "}
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />{" "}
        </div>{" "}
      </div>{" "}
      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>{" "}
    </form>
  );
}
